import { supabase } from './supabase';

/**
 * Get or create anonymous user ID
 * Stored in localStorage for persistence across sessions
 */
export function getAnonymousUserId(): string {
  const STORAGE_KEY = 'anonymous_user_id';
  
  let anonymousId = localStorage.getItem(STORAGE_KEY);
  
  if (!anonymousId) {
    // Generate a new anonymous ID
    anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(STORAGE_KEY, anonymousId);
    console.log('🆔 Generated anonymous user ID:', anonymousId);
  }
  
  return anonymousId;
}

/**
 * Get user ID (authenticated or anonymous)
 */
export function getUserId(authenticatedUserId?: string): string {
  return authenticatedUserId || getAnonymousUserId();
}

/**
 * Check if a space exists in Supabase
 */
export async function checkSpaceExists(spaceId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('spaces')
      .select('id')
      .eq('id', spaceId)
      .gt('expires_at', new Date().toISOString())
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // Not found
        return false;
      }
      console.error('Error checking space:', error);
      return false;
    }
    
    return !!data;
  } catch (err) {
    console.error('Error checking space:', err);
    return false;
  }
}

/**
 * Create a new space in Supabase
 */
export async function createSpace(spaceId: string, userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('spaces')
      .insert({
        id: spaceId,
        created_by: userId,
        peer_count: 1,
      });
    
    if (error) {
      // If space already exists (duplicate key error), that's fine
      if (error.code === '23505') {
        console.log('📍 Space already exists:', spaceId);
        return true;
      }
      console.error('Error creating space:', error);
      return false;
    }
    
    console.log('✅ Space created in Supabase:', spaceId);
    return true;
  } catch (err) {
    console.error('Error creating space:', err);
    return false;
  }
}

/**
 * Get or create a space
 * Returns the space ID if successful, null otherwise
 */
export async function getOrCreateSpace(spaceId: string, userId: string): Promise<boolean> {
  // Check if space exists
  const exists = await checkSpaceExists(spaceId);
  
  if (exists) {
    console.log('📍 Space exists:', spaceId);
    return true;
  }
  
  // Create new space
  console.log('🆕 Creating new space:', spaceId);
  return await createSpace(spaceId, userId);
}

/**
 * Update peer count in space
 */
export async function updateSpacePeerCount(spaceId: string, peerCount: number): Promise<void> {
  try {
    await supabase
      .from('spaces')
      .update({ peer_count: peerCount })
      .eq('id', spaceId);
  } catch (err) {
    console.warn('Error updating peer count:', err);
  }
}

// Word lists for generating memorable space IDs
const ADJECTIVES = [
  'happy', 'bright', 'quick', 'calm', 'cool', 'warm', 'swift', 'bold',
  'wise', 'kind', 'brave', 'smart', 'witty', 'fancy', 'jolly', 'sunny',
  'peppy', 'zesty', 'lively', 'merry', 'snappy', 'dapper', 'posh', 'grand',
  'lucky', 'cozy', 'royal', 'noble', 'sleek', 'sharp', 'slick', 'ultra'
];

const NOUNS = [
  'panda', 'tiger', 'eagle', 'dolphin', 'falcon', 'wolf', 'bear', 'fox',
  'lion', 'hawk', 'lynx', 'otter', 'raven', 'shark', 'cobra', 'gecko',
  'moose', 'rhino', 'bison', 'crane', 'swift', 'viper', 'zebra', 'koala',
  'dragon', 'phoenix', 'pegasus', 'unicorn', 'griffin', 'sphinx', 'hydra', 'kraken'
];

/**
 * Generate a new space ID
 * Format: adjective-noun-4digits (e.g., "happy-panda-7421")
 * Easy to remember and share verbally
 * 
 * Collision probability with 32×32×10000 = 10,240,000 combinations:
 * - At 1,000 active spaces: ~5% collision chance
 * - At 3,000 active spaces: ~37% collision chance
 * - At 5,000 active spaces: ~75% collision chance
 */
export function generateSpaceId(): string {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  const number = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  
  return `${adjective}-${noun}-${number}`;
}

/**
 * Generate a unique space ID with collision checking
 * Retries up to 5 times if collision detected
 */
export async function generateUniqueSpaceId(): Promise<string | null> {
  const maxRetries = 5;
  
  for (let i = 0; i < maxRetries; i++) {
    const spaceId = generateSpaceId();
    const exists = await checkSpaceExists(spaceId);
    
    if (!exists) {
      return spaceId;
    }
    
    console.warn(`⚠️ Space ID collision detected: ${spaceId}, retrying... (${i + 1}/${maxRetries})`);
  }
  
  console.error('❌ Failed to generate unique space ID after multiple attempts');
  return null;
}
